import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  HomeIcon,
  UserIcon,
  UserGroupIcon,
  AcademicCapIcon,
  ClipboardDocumentCheckIcon,
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  BuildingOffice2Icon,
  CurrencyDollarIcon,
  BookOpenIcon,
  StarIcon,
  TrophyIcon,
  CalendarDaysIcon,
  SparklesIcon,
  CheckCircleIcon,
  CreditCardIcon,
  ShoppingBagIcon,
  PhoneIcon,
  KeyIcon,
  CogIcon
} from '@heroicons/react/24/outline';

const GlobalSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const searchRef = useRef(null);
  const resultsRef = useRef(null);
  const navigate = useNavigate();
  const { organization, profile } = useAuth();



  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('recentSearches');
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved));
      } catch (error) {
        console.error('Error loading recent searches:', error);
      }
    }
  }, []);

  // Handle click outside to close search results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target) &&
        resultsRef.current &&
        !resultsRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Save search to recent searches
  const saveRecentSearch = (searchTerm, result) => {
    const recentSearch = {
      id: Date.now(),
      query: searchTerm,
      result: {
        title: result.title,
        subtitle: result.subtitle,
        type: result.type,
        href: result.href
      },
      timestamp: new Date().toISOString()
    };

    const updated = [recentSearch, ...recentSearches.filter(s => s.query !== searchTerm)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('recentSearches', JSON.stringify(updated));
  };

  // Perform search across multiple entities
  // Get navigation items based on user role
  const getNavigationItems = (userRole) => {
    const baseItems = [
      {
        id: 'dashboard',
        title: 'Dashboard',
        description: 'Main dashboard and overview',
        icon: HomeIcon,
        href: '/dashboard',
        keywords: ['home', 'main', 'overview', 'summary']
      }
    ];

    // Super Admin specific items
    if (userRole === 'Super Admin') {
      baseItems.push(
        {
          id: 'organizations',
          title: 'Organizations',
          description: 'Manage all organizations',
          icon: BuildingOffice2Icon,
          href: '/super-admin/organizations',
          keywords: ['organization', 'companies', 'businesses']
        },
        {
          id: 'financial',
          title: 'Financial Dashboard',
          description: 'Financial overview and billing',
          icon: CurrencyDollarIcon,
          href: '/super-admin/financial',
          keywords: ['financial', 'billing', 'payments', 'money']
        },
        {
          id: 'users',
          title: 'Users',
          description: 'Manage system users',
          icon: UserGroupIcon,
          href: '/super-admin/users',
          keywords: ['users', 'accounts', 'management']
        }
      );
    }

    // Management section
    if (['Organization Owner', 'Center Manager'].includes(userRole)) {
      baseItems.push(
        {
          id: 'centers',
          title: 'Centers',
          description: 'Manage training centers',
          icon: BuildingOffice2Icon,
          href: '/centers',
          keywords: ['center', 'location', 'branch', 'facility']
        }
      );

      // Organization Owner specific
      if (userRole === 'Organization Owner') {
        baseItems.push(
          {
            id: 'managers',
            title: 'Managers',
            description: 'Manage center managers',
            icon: UserIcon,
            href: '/managers',
            keywords: ['manager', 'staff', 'administration']
          }
        );
      }

      baseItems.push(
        {
          id: 'classes',
          title: 'Classes',
          description: 'View and manage classes and schedules',
          icon: AcademicCapIcon,
          href: '/classes',
          keywords: ['class', 'schedule', 'lessons', 'training']
        },
        {
          id: 'instructors',
          title: 'Instructors',
          description: 'Manage coaching staff',
          icon: UserIcon,
          href: '/coaches',
          keywords: ['coach', 'instructor', 'teacher', 'staff']
        },
        {
          id: 'parents',
          title: 'Parents',
          description: 'Manage parent accounts',
          icon: UserGroupIcon,
          href: '/parents',
          keywords: ['parent', 'guardian', 'family']
        },
        {
          id: 'students',
          title: 'Students',
          description: 'Manage student information and records',
          icon: UserGroupIcon,
          href: '/students',
          keywords: ['student', 'pupils', 'learners', 'members']
        }
      );
    } else if (userRole === 'Coach') {
      baseItems.push(
        {
          id: 'classes',
          title: 'Classes',
          description: 'View and manage classes',
          icon: AcademicCapIcon,
          href: '/classes',
          keywords: ['class', 'schedule', 'lessons', 'training']
        },
        {
          id: 'students',
          title: 'Students',
          description: 'View student information',
          icon: UserGroupIcon,
          href: '/students',
          keywords: ['student', 'pupils', 'learners', 'members']
        }
      );
    } else if (userRole === 'Parent') {
      baseItems.push(
        {
          id: 'children',
          title: 'My Children',
          description: 'View your children\'s information',
          icon: UserGroupIcon,
          href: '/students',
          keywords: ['children', 'kids', 'family', 'student']
        },
        {
          id: 'classes',
          title: 'My Classes',
          description: 'View your children\'s classes',
          icon: AcademicCapIcon,
          href: '/classes',
          keywords: ['class', 'schedule', 'lessons']
        }
      );
    } else if (userRole === 'Student') {
      baseItems[0] = {
        id: 'dashboard',
        title: 'My Dashboard',
        description: 'Student dashboard',
        icon: HomeIcon,
        href: '/student-dashboard',
        keywords: ['home', 'main', 'overview', 'summary']
      };
      baseItems.push(
        {
          id: 'classes',
          title: 'My Classes',
          description: 'View your classes',
          icon: AcademicCapIcon,
          href: '/classes',
          keywords: ['class', 'schedule', 'lessons']
        },
        {
          id: 'progress',
          title: 'My Progress',
          description: 'View your training progress',
          icon: TrophyIcon,
          href: '/student-progress',
          keywords: ['progress', 'achievement', 'growth']
        }
      );
    }

    // Schedule section
    if (['Organization Owner', 'Center Manager', 'Coach', 'Parent', 'Student'].includes(userRole)) {
      if (userRole === 'Student') {
        baseItems.push(
          {
            id: 'schedule',
            title: 'My Schedule',
            description: 'View your class schedule',
            icon: CalendarDaysIcon,
            href: '/upcoming-classes',
            keywords: ['schedule', 'calendar', 'upcoming', 'timetable']
          }
        );
      } else {
        baseItems.push(
          {
            id: 'schedule',
            title: 'Upcoming Classes',
            description: 'View upcoming class schedule',
            icon: CalendarDaysIcon,
            href: '/upcoming-classes',
            keywords: ['schedule', 'calendar', 'upcoming', 'timetable']
          }
        );
      }
    }

    // Special Programs
    if (['Organization Owner', 'Center Manager', 'Parent'].includes(userRole)) {
      if (userRole === 'Parent') {
        baseItems.push(
          {
            id: 'special-classes',
            title: 'Special Classes',
            description: 'View and enroll in special programs',
            icon: SparklesIcon,
            href: '/special-classes/parent',
            keywords: ['special', 'programs', 'events', 'workshops']
          }
        );
      } else {
        baseItems.push(
          {
            id: 'special-classes',
            title: 'Special Classes',
            description: 'Manage special programs and events',
            icon: SparklesIcon,
            href: '/special-classes',
            keywords: ['special', 'programs', 'events', 'workshops']
          }
        );
      }
    }

    // Training & Curriculum
    baseItems.push(
      {
        id: 'syllabus',
        title: 'Syllabus',
        description: 'View training syllabus and curriculum',
        icon: BookOpenIcon,
        href: '/syllabus',
        keywords: ['syllabus', 'curriculum', 'training', 'course']
      },
      {
        id: 'belt-levels',
        title: 'Belt Levels',
        description: 'View belt progression system',
        icon: StarIcon,
        href: '/belt-levels',
        keywords: ['belt', 'level', 'rank', 'progression', 'grade']
      }
    );

    // Student Progress - available to all roles
    if (['Organization Owner', 'Center Manager', 'Coach', 'Parent', 'Student'].includes(userRole)) {
      if (!userRole === 'Student') { // We already added progress for students above
        baseItems.push(
          {
            id: 'student-progress',
            title: 'Student Progress',
            description: 'Track student development and achievements',
            icon: TrophyIcon,
            href: '/student-progress',
            keywords: ['progress', 'achievement', 'development', 'growth']
          }
        );
      }
    }

    // Attendance
    if (['Organization Owner', 'Center Manager', 'Coach', 'Parent'].includes(userRole)) {
      // Only instructors, managers, and owners can mark attendance
      if (['Organization Owner', 'Center Manager', 'Coach'].includes(userRole)) {
        baseItems.push(
          {
            id: 'mark-attendance',
            title: 'Mark Attendance',
            description: 'Record student attendance',
            icon: CheckCircleIcon,
            href: '/mark-attendance',
            keywords: ['attendance', 'present', 'absent', 'roll call', 'mark']
          }
        );
      }

      // All roles (including parents) can view attendance reports
      baseItems.push(
        {
          id: 'attendance-report',
          title: 'Attendance Report',
          description: 'View attendance statistics and reports',
          icon: ClipboardDocumentCheckIcon,
          href: '/attendance-report',
          keywords: ['attendance', 'report', 'statistics', 'analytics']
        }
      );
    }

    // Communication
    baseItems.push(
      {
        id: 'messages',
        title: 'Messages',
        description: 'Communication and messaging center',
        icon: ChatBubbleLeftRightIcon,
        href: '/messages',
        keywords: ['message', 'communication', 'chat', 'notifications']
      }
    );

    // Financial
    if (['Organization Owner', 'Center Manager', 'Parent'].includes(userRole)) {
      if (userRole === 'Parent') {
        baseItems.push(
          {
            id: 'bills',
            title: 'My Bills',
            description: 'View your payment history and bills',
            icon: CreditCardIcon,
            href: '/payments',
            keywords: ['bills', 'payment', 'fees', 'billing', 'history']
          },
          {
            id: 'one-time-payments',
            title: 'My One-Time Payments',
            description: 'View one-time payment history',
            icon: ShoppingBagIcon,
            href: '/one-time-payments',
            keywords: ['payment', 'one-time', 'fees', 'billing']
          }
        );
      } else {
        baseItems.push(
          {
            id: 'monthly-payments',
            title: 'Monthly Payments',
            description: 'Manage monthly billing and payments',
            icon: CreditCardIcon,
            href: '/payments',
            keywords: ['payment', 'monthly', 'billing', 'fees']
          },
          {
            id: 'one-time-payments',
            title: 'One-Time Payments',
            description: 'Manage one-time payments and fees',
            icon: ShoppingBagIcon,
            href: '/one-time-payments',
            keywords: ['payment', 'one-time', 'fees', 'billing']
          }
        );
      }
    }

    // Reports
    if (['Organization Owner', 'Center Manager'].includes(userRole)) {
      baseItems.push(
        {
          id: 'tournament-selection',
          title: 'Tournament Selection',
          description: 'Manage tournament participation',
          icon: TrophyIcon,
          href: '/tournament-selection',
          keywords: ['tournament', 'competition', 'selection']
        },
        {
          id: 'contact-list',
          title: 'Contact List',
          description: 'Generate contact lists and reports',
          icon: PhoneIcon,
          href: '/contact-list',
          keywords: ['contact', 'list', 'phone', 'directory']
        },
        {
          id: 'center-statistics',
          title: 'Center Statistics',
          description: 'View center performance statistics',
          icon: ChartBarIcon,
          href: '/center-statistics',
          keywords: ['statistics', 'analytics', 'report', 'data', 'performance']
        }
      );
    }

    // Configuration
    if (['Organization Owner', 'Center Manager'].includes(userRole)) {
      baseItems.push(
        {
          id: 'registration-tokens',
          title: 'Registration Tokens',
          description: 'Manage registration tokens',
          icon: KeyIcon,
          href: '/token-management',
          keywords: ['token', 'registration', 'access', 'invite']
        }
      );

      // Organization Settings for organization owners only
      if (userRole === 'Organization Owner') {
        baseItems.push(
          {
            id: 'organization-settings',
            title: 'Organization Settings',
            description: 'Configure organization settings',
            icon: CogIcon,
            href: '/organization-settings',
            keywords: ['settings', 'configuration', 'organization', 'setup']
          }
        );
      }
    }

    return baseItems;
  };

  const performSearch = async (searchTerm) => {
    if (!searchTerm.trim()) return [];

    setLoading(true);
    const searchResults = [];

    try {
      // Get navigation items based on user role
      // Convert database role to display format
      const userRole = profile?.role || 'student';
      let displayRole = 'Student';

      switch (userRole) {
        case 'super_admin':
          displayRole = 'Super Admin';
          break;
        case 'organization_owner':
          displayRole = 'Organization Owner';
          break;
        case 'center_manager':
          displayRole = 'Center Manager';
          break;
        case 'coach':
          displayRole = 'Coach';
          break;
        case 'parent':
          displayRole = 'Parent';
          break;
        case 'student':
          displayRole = 'Student';
          break;
        default:
          displayRole = 'Student';
      }

      console.log('User role for search:', userRole, '-> Display role:', displayRole);

      console.log('User role for search:', userRole, '-> Display role:', displayRole);
      const navigationItems = getNavigationItems(displayRole);      // Filter navigation items that match search term
      const matchingNavItems = navigationItems.filter(item => {
        const searchLower = searchTerm.toLowerCase();
        return (
          item.title.toLowerCase().includes(searchLower) ||
          item.description.toLowerCase().includes(searchLower) ||
          (item.keywords && item.keywords.some(keyword => keyword.toLowerCase().includes(searchLower)))
        );
      });

      // Add matching navigation items to results
      matchingNavItems.forEach(item => {
        searchResults.push({
          id: `nav-${item.id}`,
          title: item.title,
          subtitle: item.description,
          type: 'navigation',
          icon: item.icon,
          href: item.href,
          data: item
        });
      });

    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }

    setResults(searchResults);

    return searchResults.sort((a, b) => {
      // Sort by relevance - exact matches first
      const aExact = a.title.toLowerCase() === searchTerm.toLowerCase();
      const bExact = b.title.toLowerCase() === searchTerm.toLowerCase();
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;

      // Then alphabetically
      return a.title.localeCompare(b.title);
    });
  };

  // Handle search input change
  const handleSearchChange = async (value) => {
    setQuery(value);
    setIsOpen(true);

    if (value.trim().length < 2) {
      setResults([]);
      return;
    }

    const searchResults = await performSearch(value);
    console.log('Search results:', searchResults);
    setResults(searchResults);
  };

  // Handle search result selection
  const handleResultSelect = (result) => {
    saveRecentSearch(query, result);
    navigate(result.href);
    setIsOpen(false);
    setQuery('');
  };

  // Handle recent search selection
  const handleRecentSearchSelect = (recentSearch) => {
    setQuery(recentSearch.query);
    navigate(recentSearch.result.href);
    setIsOpen(false);
    setQuery('');
  };

  // Clear recent searches
  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('recentSearches');
  };

  const showResults = isOpen && (results.length > 0 || query.trim().length >= 2 || recentSearches.length > 0);

  return (
    <div className="relative">
      {/* Search Input */}
      <div ref={searchRef} className="relative">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Quick Search..."
            value={query}
            onChange={(e) => handleSearchChange(e.target.value)}
            onFocus={() => setIsOpen(true)}
            className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:border-blue-500 transition-colors"
          />
          {query && (
            <button
              onClick={() => {
                setQuery('');
                setResults([]);
                setIsOpen(false);
              }}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Search Results Dropdown */}
      {showResults && (
        <div
          ref={resultsRef}
          className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto"
        >
          {loading && (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-sm">Searching...</p>
            </div>
          )}

          {!loading && query.trim().length >= 2 && results.length === 0 && (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              <p className="text-sm">No results found for "{query}"</p>
            </div>
          )}

          {!loading && results.length > 0 && (
            <div>
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide border-b border-gray-200 dark:border-gray-700">
                Search Results
              </div>
              {results.map((result) => {
                const Icon = result.icon;
                return (
                  <button
                    key={result.id}
                    onClick={() => handleResultSelect(result)}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                  >
                    <div className="flex items-center space-x-3">
                      <Icon className="h-5 w-5 text-gray-400" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {result.title}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {result.subtitle}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {query.trim().length < 2 && recentSearches.length > 0 && (
            <div>
              <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Recent Searches
                </span>
                <button
                  onClick={clearRecentSearches}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                >
                  Clear
                </button>
              </div>
              {recentSearches.map((recentSearch) => (
                <button
                  key={recentSearch.id}
                  onClick={() => handleRecentSearchSelect(recentSearch)}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                >
                  <div className="flex items-center space-x-3">
                    <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {recentSearch.result.title}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {recentSearch.result.subtitle}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;